// =============================================================================
// FIELD CONFIG PANEL - Professional configuration panel like SurveyJS
// =============================================================================

import { X, Plus, Trash2, GripVertical, Settings2, Palette, Shield, Workflow } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Separator } from '../../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { Slider } from '../../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { useFormBuilderStore } from '../store';
import type { FormField, FieldOption, ConditionalLogic } from '../types';

export function FieldConfigPanel() {
  const {
    sections,
    selectedFieldId,
    selectField,
    updateField,
    addValidation,
    removeValidation,
    addCondition,
    removeCondition,
  } = useFormBuilderStore();

  // Find selected field across all sections
  let selectedField: FormField | undefined;
  let sectionId: string | undefined;

  for (const section of sections) {
    const field = section.fields.find((f) => f.id === selectedFieldId);
    if (field) {
      selectedField = field;
      sectionId = section.id;
      break;
    }
  }

  // Get all available fields for conditional logic (exclude current field)
  const availableFields = sections.flatMap((s) =>
    s.fields.filter((f) => f.id !== selectedFieldId).map((f) => ({
      id: f.id,
      label: f.label,
      name: f.name,
    }))
  );

  if (!selectedField || !sectionId) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-6 bg-secondary-50/50 dark:bg-secondary-900/50">
        <Settings2 className="h-12 w-12 text-secondary-300 dark:text-secondary-600 mb-4" />
        <p className="text-secondary-500 font-medium">Sélectionnez un champ</p>
        <p className="text-secondary-400 text-sm mt-1">
          pour modifier ses propriétés
        </p>
      </div>
    );
  }

  const handleUpdate = (updates: Partial<FormField>) => {
    updateField(sectionId!, selectedField!.id, updates);
  };

  const handleAddOption = () => {
    const newOptions = [
      ...(selectedField?.options || []),
      { label: `Option ${(selectedField?.options?.length || 0) + 1}`, value: `option${Date.now()}` },
    ];
    handleUpdate({ options: newOptions });
  };

  const handleUpdateOption = (index: number, updates: Partial<FieldOption>) => {
    const newOptions = [...(selectedField?.options || [])];
    const current = newOptions[index];
    if (current) {
      newOptions[index] = { ...current, ...updates };
      handleUpdate({ options: newOptions });
    }
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = (selectedField?.options || []).filter((_, i) => i !== index);
    handleUpdate({ options: newOptions });
  };

  const handleAddMatrixRow = () => {
    const newRows = [
      ...(selectedField?.matrixRows || []),
      { value: `row${Date.now()}`, label: `Ligne ${(selectedField?.matrixRows?.length || 0) + 1}` },
    ];
    handleUpdate({ matrixRows: newRows });
  };

  const handleAddMatrixColumn = () => {
    const newCols = [
      ...(selectedField?.matrixColumns || []),
      { value: `col${Date.now()}`, label: `Colonne ${(selectedField?.matrixColumns?.length || 0) + 1}` },
    ];
    handleUpdate({ matrixColumns: newCols });
  };

  const handleAddCondition = () => {
    if (availableFields.length === 0) return;

    const newCondition: ConditionalLogic = {
      fieldId: availableFields[0]?.id || '',
      operator: 'equals',
      value: '',
      action: 'show',
    };
    addCondition(sectionId!, selectedField!.id, newCondition);
  };

  const hasOptions = ['select', 'multiselect', 'radio', 'checkboxgroup', 'ranking'].includes(selectedField.type);
  const hasMatrix = selectedField.type === 'matrix';
  const hasSliderConfig = selectedField.type === 'slider';
  const hasRatingConfig = selectedField.type === 'rating';
  const hasTextareaConfig = selectedField.type === 'textarea';
  const hasHeadingConfig = selectedField.type === 'heading';
  const hasHtmlConfig = selectedField.type === 'html';
  const hasFileConfig = ['file', 'image'].includes(selectedField.type);

  const isRequired = selectedField.validation.some((v) => v.type === 'required');

  // Get field type label
  const getFieldTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      text: 'Texte',
      email: 'Email',
      number: 'Nombre',
      phone: 'Téléphone',
      url: 'URL',
      password: 'Mot de passe',
      textarea: 'Texte long',
      select: 'Liste',
      multiselect: 'Multi-sélection',
      checkbox: 'Case à cocher',
      checkboxgroup: 'Groupe de cases',
      radio: 'Boutons radio',
      boolean: 'Oui/Non',
      date: 'Date',
      time: 'Heure',
      datetime: 'Date et heure',
      file: 'Fichier',
      image: 'Image',
      signature: 'Signature',
      rating: 'Étoiles',
      nps: 'NPS',
      slider: 'Curseur',
      ranking: 'Classement',
      matrix: 'Matrice',
      heading: 'Titre',
      paragraph: 'Paragraphe',
      html: 'HTML',
      'image-display': 'Afficher image',
      divider: 'Séparateur',
    };
    return labels[type] || type;
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-secondary-800">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {getFieldTypeLabel(selectedField.type)}
          </Badge>
          <h3 className="font-semibold text-secondary-900 dark:text-white truncate max-w-[150px]">
            {selectedField.label}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => selectField(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="px-4 pt-2 justify-start bg-transparent border-b border-secondary-200 dark:border-secondary-700 rounded-none">
          <TabsTrigger value="general" className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            Général
          </TabsTrigger>
          <TabsTrigger value="validation" className="gap-1.5">
            <Shield className="h-3.5 w-3.5" />
            Validation
          </TabsTrigger>
          <TabsTrigger value="logic" className="gap-1.5">
            <Workflow className="h-3.5 w-3.5" />
            Logique
          </TabsTrigger>
          <TabsTrigger value="appearance" className="gap-1.5">
            <Palette className="h-3.5 w-3.5" />
            Style
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          {/* Basic info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Libellé</Label>
              <Input
                value={selectedField.label}
                onChange={(e) => handleUpdate({ label: e.target.value })}
                placeholder="Libellé du champ"
              />
            </div>

            <div className="space-y-2">
              <Label>Nom technique</Label>
              <Input
                value={selectedField.name}
                onChange={(e) => handleUpdate({ name: e.target.value })}
                placeholder="nom_du_champ"
                className="font-mono text-sm"
              />
              <p className="text-xs text-secondary-500">
                Utilisé pour identifier le champ dans les données
              </p>
            </div>

            {!['divider', 'heading', 'paragraph', 'html', 'image-display'].includes(selectedField.type) && (
              <div className="space-y-2">
                <Label>Placeholder</Label>
                <Input
                  value={selectedField.placeholder || ''}
                  onChange={(e) => handleUpdate({ placeholder: e.target.value })}
                  placeholder="Texte d'indication..."
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Texte d'aide</Label>
              <Textarea
                value={selectedField.helpText || ''}
                onChange={(e) => handleUpdate({ helpText: e.target.value })}
                placeholder="Instructions pour l'utilisateur..."
                rows={2}
              />
            </div>

            {['paragraph', 'checkbox'].includes(selectedField.type) && (
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={selectedField.description || ''}
                  onChange={(e) => handleUpdate({ description: e.target.value })}
                  placeholder="Description du champ..."
                  rows={3}
                />
              </div>
            )}
          </div>

          <Separator />

          {/* Options for select, radio, checkbox group */}
          {hasOptions && (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Options</Label>
                  <Button variant="outline" size="sm" onClick={handleAddOption}>
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {selectedField.options?.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg bg-secondary-50 dark:bg-secondary-900"
                    >
                      <GripVertical className="h-4 w-4 text-secondary-400 cursor-grab" />
                      <Input
                        value={option.label}
                        onChange={(e) =>
                          handleUpdateOption(index, {
                            label: e.target.value,
                            value: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                          })
                        }
                        placeholder="Libellé de l'option"
                        className="flex-1 h-8"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {(!selectedField.options || selectedField.options.length === 0) && (
                    <p className="text-sm text-secondary-500 text-center py-4">
                      Aucune option. Cliquez sur "Ajouter" pour créer des options.
                    </p>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Matrix configuration */}
          {hasMatrix && (
            <>
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Lignes</Label>
                    <Button variant="outline" size="sm" onClick={handleAddMatrixRow}>
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {selectedField.matrixRows?.map((row, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={row.label}
                          onChange={(e) => {
                            const newRows = [...(selectedField.matrixRows || [])];
                            const currentRow = newRows[index];
                            if (currentRow) {
                              newRows[index] = { ...currentRow, label: e.target.value };
                              handleUpdate({ matrixRows: newRows });
                            }
                          }}
                          placeholder="Libellé de la ligne"
                          className="flex-1 h-8"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => {
                            const newRows = selectedField.matrixRows?.filter((_, i) => i !== index) || [];
                            handleUpdate({ matrixRows: newRows });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Colonnes</Label>
                    <Button variant="outline" size="sm" onClick={handleAddMatrixColumn}>
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {selectedField.matrixColumns?.map((col, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={col.label}
                          onChange={(e) => {
                            const newCols = [...(selectedField.matrixColumns || [])];
                            const currentCol = newCols[index];
                            if (currentCol) {
                              newCols[index] = { ...currentCol, label: e.target.value };
                              handleUpdate({ matrixColumns: newCols });
                            }
                          }}
                          placeholder="Libellé de la colonne"
                          className="flex-1 h-8"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => {
                            const newCols = selectedField.matrixColumns?.filter((_, i) => i !== index) || [];
                            handleUpdate({ matrixColumns: newCols });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Slider configuration */}
          {hasSliderConfig && (
            <>
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Configuration du curseur</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Valeur min</Label>
                    <Input
                      type="number"
                      value={selectedField.minValue ?? 0}
                      onChange={(e) => handleUpdate({ minValue: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valeur max</Label>
                    <Input
                      type="number"
                      value={selectedField.maxValue ?? 100}
                      onChange={(e) => handleUpdate({ maxValue: parseInt(e.target.value) || 100 })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Pas (step)</Label>
                  <Input
                    type="number"
                    value={selectedField.step ?? 1}
                    onChange={(e) => handleUpdate({ step: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Afficher la valeur</Label>
                  <Switch
                    checked={selectedField.showValue ?? true}
                    onCheckedChange={(checked) => handleUpdate({ showValue: checked })}
                  />
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Rating configuration */}
          {hasRatingConfig && (
            <>
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Configuration des étoiles</h4>
                <div className="space-y-2">
                  <Label>Nombre d'étoiles</Label>
                  <Select
                    value={String(selectedField.ratingCount ?? 5)}
                    onValueChange={(value) => handleUpdate({ ratingCount: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 étoiles</SelectItem>
                      <SelectItem value="5">5 étoiles</SelectItem>
                      <SelectItem value="7">7 étoiles</SelectItem>
                      <SelectItem value="10">10 étoiles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Textarea configuration */}
          {hasTextareaConfig && (
            <>
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Configuration du texte long</h4>
                <div className="space-y-2">
                  <Label>Nombre de lignes</Label>
                  <Slider
                    value={[selectedField.rows ?? 4]}
                    min={2}
                    max={10}
                    step={1}
                    onValueChange={(value: number[]) => handleUpdate({ rows: value[0] ?? 4 })}
                  />
                  <p className="text-xs text-secondary-500 text-right">
                    {selectedField.rows ?? 4} lignes
                  </p>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Heading configuration */}
          {hasHeadingConfig && (
            <>
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Configuration du titre</h4>
                <div className="space-y-2">
                  <Label>Niveau du titre</Label>
                  <Select
                    value={selectedField.headingLevel ?? 'h2'}
                    onValueChange={(value) => handleUpdate({ headingLevel: value as 'h1' | 'h2' | 'h3' | 'h4' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="h1">H1 - Très grand</SelectItem>
                      <SelectItem value="h2">H2 - Grand</SelectItem>
                      <SelectItem value="h3">H3 - Moyen</SelectItem>
                      <SelectItem value="h4">H4 - Petit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* HTML configuration */}
          {hasHtmlConfig && (
            <>
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Contenu HTML</h4>
                <div className="space-y-2">
                  <Textarea
                    value={selectedField.htmlContent || ''}
                    onChange={(e) => handleUpdate({ htmlContent: e.target.value })}
                    placeholder="<p>Votre contenu HTML...</p>"
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-secondary-500">
                    Utilisez du HTML valide pour afficher du contenu personnalisé
                  </p>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* File upload configuration */}
          {hasFileConfig && (
            <>
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Configuration du fichier</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Fichiers max</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={selectedField.maxFiles ?? 1}
                      onChange={(e) => handleUpdate({ maxFiles: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Taille max (Mo)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={selectedField.maxFileSize ?? 10}
                      onChange={(e) => handleUpdate({ maxFileSize: parseInt(e.target.value) || 10 })}
                    />
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Boolean options */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-secondary-900 dark:text-white">
              Options
            </h4>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Lecture seule</Label>
                <p className="text-xs text-secondary-500">
                  Le champ ne peut pas être modifié
                </p>
              </div>
              <Switch
                checked={selectedField.readOnly ?? false}
                onCheckedChange={(checked) => handleUpdate({ readOnly: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Masqué</Label>
                <p className="text-xs text-secondary-500">
                  Le champ n'est pas visible
                </p>
              </div>
              <Switch
                checked={selectedField.hidden ?? false}
                onCheckedChange={(checked) => handleUpdate({ hidden: checked })}
              />
            </div>
          </div>
        </TabsContent>

        {/* Validation Tab */}
        <TabsContent value="validation" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary-50 dark:bg-secondary-900">
              <div>
                <Label className="text-base">Obligatoire</Label>
                <p className="text-xs text-secondary-500">
                  Ce champ doit être rempli
                </p>
              </div>
              <Switch
                checked={isRequired}
                onCheckedChange={(checked) => {
                  if (checked) {
                    addValidation(sectionId!, selectedField!.id, {
                      type: 'required',
                      message: 'Ce champ est obligatoire',
                    });
                  } else {
                    const index = selectedField!.validation.findIndex((v) => v.type === 'required');
                    if (index !== -1) {
                      removeValidation(sectionId!, selectedField!.id, index);
                    }
                  }
                }}
              />
            </div>

            {['text', 'textarea', 'password'].includes(selectedField.type) && (
              <>
                <Separator />
                <h4 className="text-sm font-medium">Longueur du texte</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Min caractères</Label>
                    <Input
                      type="number"
                      min={0}
                      value={
                        (selectedField.validation.find((v) => v.type === 'minLength')?.value as number) || ''
                      }
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        const existingIndex = selectedField!.validation.findIndex(
                          (v) => v.type === 'minLength'
                        );
                        if (existingIndex !== -1) {
                          removeValidation(sectionId!, selectedField!.id, existingIndex);
                        }
                        if (!isNaN(value) && value > 0) {
                          addValidation(sectionId!, selectedField!.id, {
                            type: 'minLength',
                            value,
                            message: `Minimum ${value} caractères`,
                          });
                        }
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max caractères</Label>
                    <Input
                      type="number"
                      min={0}
                      value={
                        (selectedField.validation.find((v) => v.type === 'maxLength')?.value as number) || ''
                      }
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        const existingIndex = selectedField!.validation.findIndex(
                          (v) => v.type === 'maxLength'
                        );
                        if (existingIndex !== -1) {
                          removeValidation(sectionId!, selectedField!.id, existingIndex);
                        }
                        if (!isNaN(value) && value > 0) {
                          addValidation(sectionId!, selectedField!.id, {
                            type: 'maxLength',
                            value,
                            message: `Maximum ${value} caractères`,
                          });
                        }
                      }}
                      placeholder="Illimité"
                    />
                  </div>
                </div>
              </>
            )}

            {selectedField.type === 'number' && (
              <>
                <Separator />
                <h4 className="text-sm font-medium">Plage de valeurs</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Valeur min</Label>
                    <Input
                      type="number"
                      value={
                        (selectedField.validation.find((v) => v.type === 'min')?.value as number) || ''
                      }
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        const existingIndex = selectedField!.validation.findIndex((v) => v.type === 'min');
                        if (existingIndex !== -1) {
                          removeValidation(sectionId!, selectedField!.id, existingIndex);
                        }
                        if (!isNaN(value)) {
                          addValidation(sectionId!, selectedField!.id, {
                            type: 'min',
                            value,
                            message: `Minimum ${value}`,
                          });
                        }
                      }}
                      placeholder="Aucun minimum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valeur max</Label>
                    <Input
                      type="number"
                      value={
                        (selectedField.validation.find((v) => v.type === 'max')?.value as number) || ''
                      }
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        const existingIndex = selectedField!.validation.findIndex((v) => v.type === 'max');
                        if (existingIndex !== -1) {
                          removeValidation(sectionId!, selectedField!.id, existingIndex);
                        }
                        if (!isNaN(value)) {
                          addValidation(sectionId!, selectedField!.id, {
                            type: 'max',
                            value,
                            message: `Maximum ${value}`,
                          });
                        }
                      }}
                      placeholder="Aucun maximum"
                    />
                  </div>
                </div>
              </>
            )}

            {selectedField.type === 'text' && (
              <>
                <Separator />
                <h4 className="text-sm font-medium">Expression régulière</h4>
                <div className="space-y-2">
                  <Input
                    value={
                      (selectedField.validation.find((v) => v.type === 'pattern')?.value as string) || ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      const existingIndex = selectedField!.validation.findIndex((v) => v.type === 'pattern');
                      if (existingIndex !== -1) {
                        removeValidation(sectionId!, selectedField!.id, existingIndex);
                      }
                      if (value) {
                        addValidation(sectionId!, selectedField!.id, {
                          type: 'pattern',
                          value,
                          message: 'Format invalide',
                        });
                      }
                    }}
                    placeholder="^[A-Z].*"
                    className="font-mono"
                  />
                  <p className="text-xs text-secondary-500">
                    Ex: ^[A-Z].* pour commencer par une majuscule
                  </p>
                </div>
              </>
            )}

            <Separator />

            <div className="space-y-3">
              <h4 className="text-sm font-medium">Règles actives</h4>
              {selectedField.validation.length === 0 ? (
                <p className="text-sm text-secondary-500 text-center py-4">
                  Aucune règle de validation
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedField.validation.map((rule, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded-lg bg-secondary-50 dark:bg-secondary-900"
                    >
                      <div>
                        <Badge variant="outline" className="text-xs">
                          {rule.type}
                        </Badge>
                        {rule.value !== undefined && (
                          <span className="ml-2 text-sm text-secondary-600">
                            : {String(rule.value)}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500"
                        onClick={() => removeValidation(sectionId!, selectedField!.id, index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Logic Tab */}
        <TabsContent value="logic" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium">Logique conditionnelle</h4>
                <p className="text-xs text-secondary-500">
                  Afficher/masquer ce champ selon des conditions
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddCondition}
                disabled={availableFields.length === 0}
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter
              </Button>
            </div>

            {availableFields.length === 0 && (
              <div className="text-center py-6 text-secondary-500">
                <p className="text-sm">
                  Ajoutez d'autres champs pour créer des conditions
                </p>
              </div>
            )}

            {(selectedField.conditions?.length || 0) === 0 && availableFields.length > 0 && (
              <div className="text-center py-6 text-secondary-500">
                <Workflow className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  Aucune condition définie
                </p>
              </div>
            )}

            {selectedField.conditions?.map((condition, index) => (
              <div
                key={index}
                className="p-4 rounded-lg border border-secondary-200 dark:border-secondary-700 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">Condition {index + 1}</Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500"
                    onClick={() => removeCondition(sectionId!, selectedField!.id, index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Si le champ</Label>
                  <Select
                    value={condition.fieldId}
                    onValueChange={(value) => {
                      const newConditions = [...(selectedField.conditions || [])];
                      const currentCondition = newConditions[index];
                      if (currentCondition) {
                        newConditions[index] = { ...currentCondition, fieldId: value };
                        handleUpdate({ conditions: newConditions });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un champ" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFields.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Opérateur</Label>
                  <Select
                    value={condition.operator}
                    onValueChange={(value) => {
                      const newConditions = [...(selectedField.conditions || [])];
                      const currentCondition = newConditions[index];
                      if (currentCondition) {
                        newConditions[index] = { ...currentCondition, operator: value as ConditionalLogic['operator'] };
                        handleUpdate({ conditions: newConditions });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Est égal à</SelectItem>
                      <SelectItem value="notEquals">N'est pas égal à</SelectItem>
                      <SelectItem value="contains">Contient</SelectItem>
                      <SelectItem value="notContains">Ne contient pas</SelectItem>
                      <SelectItem value="isEmpty">Est vide</SelectItem>
                      <SelectItem value="isNotEmpty">N'est pas vide</SelectItem>
                      <SelectItem value="greaterThan">Est supérieur à</SelectItem>
                      <SelectItem value="lessThan">Est inférieur à</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {!['isEmpty', 'isNotEmpty'].includes(condition.operator) && (
                  <div className="space-y-2">
                    <Label>Valeur</Label>
                    <Input
                      value={String(condition.value || '')}
                      onChange={(e) => {
                        const newConditions = [...(selectedField.conditions || [])];
                        const currentCondition = newConditions[index];
                        if (currentCondition) {
                          newConditions[index] = { ...currentCondition, value: e.target.value };
                          handleUpdate({ conditions: newConditions });
                        }
                      }}
                      placeholder="Valeur de comparaison"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Alors</Label>
                  <Select
                    value={condition.action}
                    onValueChange={(value) => {
                      const newConditions = [...(selectedField.conditions || [])];
                      const currentCondition = newConditions[index];
                      if (currentCondition) {
                        newConditions[index] = { ...currentCondition, action: value as ConditionalLogic['action'] };
                        handleUpdate({ conditions: newConditions });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="show">Afficher ce champ</SelectItem>
                      <SelectItem value="hide">Masquer ce champ</SelectItem>
                      <SelectItem value="enable">Activer ce champ</SelectItem>
                      <SelectItem value="disable">Désactiver ce champ</SelectItem>
                      <SelectItem value="require">Rendre obligatoire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Largeur du champ</h4>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'full', label: '100%' },
                { value: 'half', label: '50%' },
                { value: 'third', label: '33%' },
                { value: 'quarter', label: '25%' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleUpdate({ width: option.value as FormField['width'] })}
                  className={cn(
                    'p-3 rounded-lg border-2 text-center transition-all',
                    selectedField.width === option.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'border-secondary-200 hover:border-secondary-300 dark:border-secondary-700'
                  )}
                >
                  <div
                    className={cn(
                      'h-2 rounded mb-2 mx-auto',
                      selectedField.width === option.value
                        ? 'bg-primary-500'
                        : 'bg-secondary-300 dark:bg-secondary-600'
                    )}
                    style={{
                      width: option.value === 'full' ? '100%' : option.value === 'half' ? '50%' : option.value === 'third' ? '33%' : '25%',
                    }}
                  />
                  <span className="text-xs font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
